import {
  Box,
  makeStyles,
  Typography,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import { Button, StandardDialog, SelectField } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { PhotoCover } from '@roc/ui';
import { formatDate } from '@roc/feature-utils';

const useStyles = makeStyles(theme => ({
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  photoItem: {
    position: 'relative',
    cursor: 'pointer',
    border: '2px solid',
    borderRadius: '8px',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  photoItemSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 0 8px rgba(57, 188, 208, 0.5)',
  },
  photoItemUnselected: {
    borderColor: '#e0e0e0',
  },
  checkbox: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '2px',
    zIndex: 1,
  },
  photoInfo: {
    padding: '4px 8px',
    fontSize: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  selectAllButton: {
    marginTop: '8px',
  },
  actionSection: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: 'transparent',
    borderRadius: '8px',
  },
}));

const SECTION_LABEL_MAX_LENGTH = 60;

const truncate = (text: string, maxLength: number) =>
  text && text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;

interface MoveToSectionDialogProps {
  open: boolean;
  onClose: () => void;
}

export const MoveToSectionDialog = observer(
  ({ open, onClose }: MoveToSectionDialogProps) => {
    const classes = useStyles();
    const { inspectionStore } = useStore();
    const { inspectionPhotosStore, inspectionTasks } = inspectionStore;
    const inspectionPhotos = inspectionPhotosStore.inspectionPhotos || [];
    const inspectionDetails = inspectionStore.inspectionDetails;

    const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<number>>(
      new Set()
    );
    const [destinationSection, setDestinationSection] = useState('');

    useEffect(() => {
      if (open) {
        setSelectedPhotoIds(new Set());
        setDestinationSection('');
      }
    }, [open]);

    const handlePhotoToggle = (photoId: number) => {
      const newSelected = new Set(selectedPhotoIds);
      if (newSelected.has(photoId)) {
        newSelected.delete(photoId);
      } else {
        newSelected.add(photoId);
      }
      setSelectedPhotoIds(newSelected);
    };

    const handleSelectAll = () => {
      if (selectedPhotoIds.size === inspectionPhotos.length) {
        setSelectedPhotoIds(new Set());
      } else {
        setSelectedPhotoIds(
          new Set(inspectionPhotos.map(photo => photo.inspectionPhotoId))
        );
      }
    };

    const handleClose = () => {
      onClose();
    };

    const handleSubmit = async () => {
      if (!inspectionDetails?.inspectionId || !destinationSection) return;
      await inspectionPhotosStore.moveToSection(
        Array.from(selectedPhotoIds),
        inspectionDetails.inspectionId,
        parseInt(destinationSection)
      );
      onClose();
    };

    const isAllSelected =
      selectedPhotoIds.size === inspectionPhotos.length &&
      inspectionPhotos.length > 0;

    const dialogContent = (
      <Box>
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ marginBottom: '16px' }}
        >
          Please select the photographs you wish to move to another section of
          this inspection.
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleSelectAll}
          className={classes.selectAllButton}
          fullWidth
          testId="select-all-photos-to-section"
        >
          {isAllSelected ? 'DESELECT ALL PHOTOS' : 'SELECT ALL PHOTOS'}
        </Button>

        <div className={classes.photoGrid}>
          {inspectionPhotos.map(photo => {
            const isSelected = selectedPhotoIds.has(photo.inspectionPhotoId);
            return (
              <div
                key={photo.inspectionPhotoId}
                className={`${classes.photoItem} ${
                  isSelected
                    ? classes.photoItemSelected
                    : classes.photoItemUnselected
                }`}
                onClick={() => handlePhotoToggle(photo.inspectionPhotoId)}
              >
                <Checkbox
                  checked={isSelected}
                  className={classes.checkbox}
                  color="primary"
                  onClick={e => {
                    e.stopPropagation();
                    handlePhotoToggle(photo.inspectionPhotoId);
                  }}
                />
                <PhotoCover
                  url={photo.thumbnailImage || photo.image}
                  rotationDegrees={photo.preferredRotation || 0}
                  width="100%"
                  height="120px"
                  caption={photo.caption}
                />
                <Box className={classes.photoInfo}>
                  <Typography variant="caption">
                    {formatDate(photo.captureTimeIso, 'MM-dd-yyyy hh:mm:ss aaa')}
                  </Typography>
                </Box>
              </div>
            );
          })}
        </div>

        <Box className={classes.actionSection}>
          <SelectField
            label="Move to section"
            value={destinationSection}
            onChange={value => setDestinationSection(value)}
            options={(inspectionTasks || []).map(task => ({
              label: (
                <Tooltip title={task.description || ''}>
                  <span>{truncate(task.description, SECTION_LABEL_MAX_LENGTH)}</span>
                </Tooltip>
              ),
              value: task.inspectionTaskId.toString(),
            })) as any}
            fullWidth
            variant="outlined"
            testId="select-destination-section"
          />
        </Box>
      </Box>
    );

    const dialogActions = (
      <>
        <Button
          variant="outlined"
          onClick={handleClose}
          testId="cancel-move-to-section"
          disabled={inspectionPhotosStore.isLoadingMoveData}
          style={{ marginRight: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={
            inspectionPhotosStore.isLoadingMoveData ||
            selectedPhotoIds.size === 0 ||
            !destinationSection
          }
          testId="submit-move-to-section"
        >
          Move Photos
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        handleClose={handleClose}
        title="Move Photos"
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        maxWidth="sm"
        fullWidth
        removeContentBox
      />
    );
  }
);
